import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { queryKeys } from "../api/queryKeys";
import { useAppContext } from "../../contexts/AppContext";
import { handleApiError } from "../../utils/handleApiError";
import SendItemsParams from "../../types/SendItemsParams";
import { SendItemsResponse } from "../../types/api/OrderResponse";

export const useSendItems = () => {
  const queryClient = useQueryClient();
  const { getCampanhaSolarValue } = useAppContext();
  const navigate = useNavigate();

  return useMutation<SendItemsResponse, unknown, SendItemsParams>({
    mutationFn: async ({ items, sum }) => {
      const isCampanhaSolar = getCampanhaSolarValue();

      const response = await api.put<SendItemsResponse>("orders/item", {
        items,
        sum,
        isCampanhaSolar,
      });

      if (response.status !== 200) {
        throw new Error("Não foi possível adicionar os itens");
      }

      return response.data;
    },
    onSuccess: data => {
      toast.success(
        "Itens adicionados com sucesso! Clique para ver o pedido.",
        {
          onClick: () => navigate("/pedido"),
          style: { cursor: "pointer" },
        }
      );

      const isCampanhaSolar = getCampanhaSolarValue();

      const currentOrderData = queryClient.getQueryData<SendItemsResponse>(
        queryKeys.order(isCampanhaSolar)
      );

      if (currentOrderData && currentOrderData.items) {
        const updatedItemsMap = new Map(
          data.items.map(item => [item.cd_mercadoria, item])
        );

        const preservedItems = currentOrderData.items.map(item => {
          const updatedItem = updatedItemsMap.get(item.cd_mercadoria);
          if (updatedItem) {
            return { ...item, qt_pedida: updatedItem.qt_pedida };
          }
          return item;
        });

        const existingIds = new Set(
          currentOrderData.items.map(item => item.cd_mercadoria)
        );
        const newItems = data.items.filter(
          item => !existingIds.has(item.cd_mercadoria)
        );

        const allItems = [...preservedItems, ...newItems].filter(
          item => item.qt_pedida > 0
        );

        queryClient.setQueryData(queryKeys.order(isCampanhaSolar), {
          ...data,
          items: allItems,
        });
      } else {
        queryClient.setQueryData(queryKeys.order(isCampanhaSolar), data);
      }

      // Invalida apenas a query específica do isCampanhaSolar atual
      // Isso evita refetch de ambas as queries (S e N)
      queryClient.invalidateQueries({
        queryKey: queryKeys.order(isCampanhaSolar),
      });

      // Reset parcelamento é feito automaticamente pelo hook useParcelamento quando os totais mudam
    },
    onError: (error: unknown) => {
      handleApiError(error, "Não foi possível adicionar os itens");
    },
  });
};
